import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useWaitlist } from "@/context/WaitlistContext";

const JedBotMark = ({ size = 32 }) => (
  <div
    className="overflow-hidden rounded-xl shadow-[0_6px_16px_rgba(31,42,55,0.28)]"
    style={{ width: size, height: size }}
  >
    <img
      src="/jedbot-logo.jpg"
      alt="JedBotLabs logo"
      width={size}
      height={size}
      className="h-full w-full object-cover"
    />
  </div>
);

const NAV_LINKS = [
  { to: "/waitlist#live-demo", label: "Live Demo", testid: "demo" },
  { to: "/waitlist#features", label: "Features", testid: "features" },
  { to: "/about", label: "About", testid: "about" },
  { to: "/faq", label: "FAQ", testid: "faq" },
  { to: "/founder/wungnaokui-awungshi", label: "Founder", testid: "founder" },
  { to: "/contact", label: "Contact", testid: "contact" },
];

// Links shown inline on desktop (keep the top bar concise).
const DESKTOP_LINKS = NAV_LINKS.slice(0, 4);

const Navbar = () => {
  const { openWaitlist } = useWaitlist();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-slate-200/70 bg-white/80 backdrop-blur-md"
      data-testid="navbar"
    >
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-2 px-4 sm:px-6 py-3">
        <Link to="/waitlist" className="flex shrink-0 items-center gap-2.5" data-testid="navbar-logo">
          <JedBotMark size={34} />
          <span className="font-display text-lg font-semibold tracking-tight text-slate-900">JedBotLabs</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 md:flex" data-testid="navbar-desktop-nav">
          {DESKTOP_LINKS.map((l) => (
            <Link
              key={l.testid}
              to={l.to}
              className="whitespace-nowrap text-sm font-medium text-slate-600 transition-colors hover:text-slate-900"
              data-testid={`nav-${l.testid}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <Button
            onClick={openWaitlist}
            className="rounded-xl bg-[var(--jb-blue)] px-3 text-xs sm:px-4 sm:text-sm text-white shadow-[0_8px_20px_rgba(74,144,226,0.28)] transition-transform transition-colors hover:-translate-y-0.5 hover:bg-[#3E7FCA] active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-[var(--jb-mint)] focus-visible:ring-offset-2"
            data-testid="navbar-join-waitlist-button"
          >
            Join Waitlist
          </Button>

          {/* Mobile hamburger */}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--jb-blue)] md:hidden"
                aria-label="Open menu"
                data-testid="navbar-menu-button"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[82%] max-w-xs bg-white p-0" data-testid="mobile-menu">
              <SheetHeader className="border-b border-slate-100 px-5 py-4 text-left">
                <SheetTitle className="flex items-center gap-2.5">
                  <JedBotMark size={30} />
                  <span className="font-display text-base font-semibold tracking-tight text-slate-900">JedBotLabs</span>
                </SheetTitle>
              </SheetHeader>

              <nav className="flex flex-col px-3 py-3" data-testid="mobile-menu-nav">
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.testid}>
                    <Link
                      to={l.to}
                      className="rounded-xl px-3 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-[var(--jb-surface-2)] hover:text-slate-900"
                      data-testid={`mobile-nav-${l.testid}`}
                    >
                      {l.label}
                    </Link>
                  </SheetClose>
                ))}
              </nav>

              <div className="px-5 pb-6 pt-2">
                <SheetClose asChild>
                  <Button
                    onClick={openWaitlist}
                    className="w-full rounded-xl bg-[var(--jb-blue)] py-5 text-sm font-medium text-white shadow-[0_8px_20px_rgba(74,144,226,0.28)] transition-colors hover:bg-[#3E7FCA] active:scale-[0.98]"
                    data-testid="mobile-join-waitlist-button"
                  >
                    Join the Waitlist
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export { JedBotMark };
export default Navbar;
